import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, User, Phone, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface InstructorClass {
  id: string;
  day_of_week: number;
  time_slot: string;
  court_number: number;
  max_students: number;
  level: {
    name: string;
    color: string;
  } | null;
  bookings: {
    id: string;
    student_name: string;
    student_phone?: string;
    status: string;
    created_at: string;
  }[];
}

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

export default function InstructorDashboard() {
  const [instructorClasses, setInstructorClasses] = useState<InstructorClass[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [instructor, setInstructor] = useState<any>(null);

  useEffect(() => {
    checkInstructorAuth();
  }, []);

  useEffect(() => {
    if (instructor) {
      fetchInstructorClasses();
    }
  }, [instructor, selectedDate]);

  const checkInstructorAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('Debes iniciar sesión como instructor');
        return;
      }

      const { data: instructorData, error } = await supabase
        .from('instructors')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (error || !instructorData) {
        alert('No tienes permisos de instructor');
        return;
      }

      setInstructor(instructorData);
    } catch (error) {
      console.error('Error checking instructor auth:', error);
    }
  };

  const fetchInstructorClasses = async () => {
    if (!instructor) return;

    try {
      setLoading(true);
      
      const { data: schedules, error: schedulesError } = await supabase
        .from('class_schedules')
        .select(`
          *,
          level:class_levels(name, color)
        `)
        .eq('instructor_id', instructor.id)
        .eq('is_active', true)
        .order('day_of_week')
        .order('time_slot');

      if (schedulesError) throw schedulesError;

      // Obtener reservas para cada horario en la fecha seleccionada
      const classesWithBookings = await Promise.all(
        schedules.map(async (schedule) => {
          const { data: bookings, error: bookingsError } = await supabase
            .from('class_bookings')
            .select('*')
            .eq('schedule_id', schedule.id)
            .eq('booking_date', formatDate(selectedDate))
            .order('created_at');

          if (bookingsError) throw bookingsError;

          return {
            ...schedule,
            bookings: bookings || []
          };
        })
      );

      setInstructorClasses(classesWithBookings);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching instructor classes:', error);
      setLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const getClassStatus = (classItem: InstructorClass) => {
    const confirmedBookings = classItem.bookings.filter(b => b.status === 'confirmed');
    return {
      enrolled: confirmedBookings.length,
      available: classItem.max_students - confirmedBookings.length
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!instructor) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Acceso Denegado</h2>
        <p className="text-gray-600">No tienes permisos de instructor para acceder a esta sección.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Panel de Instructor</h2>
          <p className="text-gray-600">Bienvenido, {instructor.name}</p>
        </div>
        <div className="flex items-center space-x-4">
          <Calendar className="h-5 w-5 text-gray-500" />
          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
      </div>

      {instructorClasses.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">No tienes clases asignadas para esta fecha.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {DAYS.map((day, dayIndex) => {
            const dayClasses = instructorClasses.filter(c => c.day_of_week === dayIndex + 1);
            
            if (dayClasses.length === 0) return null;

            return (
              <div key={day} className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{day}</h3>
                <div className="space-y-4">
                  {dayClasses.map((classItem) => {
                    const status = getClassStatus(classItem);
                    const confirmedBookings = classItem.bookings.filter(b => b.status === 'confirmed');
                    
                    return (
                      <div key={classItem.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4 text-gray-500" />
                              <span className="font-medium">{formatTime(classItem.time_slot)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Cancha {classItem.court_number}</span>
                            </div>
                            {classItem.level && (
                              <span
                                className="px-2 py-1 text-xs rounded-full text-white"
                                style={{ backgroundColor: classItem.level.color }}
                              >
                                {classItem.level.name}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">
                              {status.enrolled}/{classItem.max_students} alumnos
                            </span>
                            {status.enrolled === classItem.max_students && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                        </div>

                        {confirmedBookings.length > 0 ? (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">Alumnos Inscritos:</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {confirmedBookings.map((booking) => (
                                <div key={booking.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <div className="flex items-center space-x-2">
                                    <User className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm font-medium">{booking.student_name}</span>
                                  </div>
                                  {booking.student_phone && (
                                    <div className="flex items-center space-x-1">
                                      <Phone className="h-3 w-3 text-gray-400" />
                                      <span className="text-xs text-gray-600">{booking.student_phone}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">No hay alumnos inscritos aún</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}